import React, { FC, FunctionComponent } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { useParams } from 'react-router-dom';
import { Section } from 'src/components/widgets/section';
import { DeleteButton, EditButton } from 'src/shared/globals';
import { stringFormat, T, toLocalID } from 'src/shared/helpers';
import styled from 'styled-components';
import EyeSlashIcon from '../../../shared/icons/eye-slash-solid.svg';
import EyeIcon from '../../../shared/icons/eye-solid.svg';
import { PrimaryButton } from '../../widgets/buttons';
import { Dialog, DialogWindow, QuestionDialog, useDialogManager } from '../../widgets/dialog';
import { EmptyList } from '../../widgets/emptyList';
import { Icon } from '../../widgets/icon';
import { Container, VerticalLayout } from '../../widgets/layouts';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from '../../widgets/notificationToaster';
import { PageHeader } from '../../widgets/pageHeader';
import { LinkCell, Row, Table, TableHeading } from '../../widgets/table';
import PlacementPage from '../placement/placement';
import { placementsDeleteMutation } from './__generated__/placementsDeleteMutation.graphql';
import { placementsEnablePlacementMutation } from './__generated__/placementsEnablePlacementMutation.graphql';
import { placementsQuery } from './__generated__/placementsQuery.graphql';

export const EyeButton: FunctionComponent<{ enabled: boolean; onClick: () => void }> = ({ enabled, onClick }) => {
    return (
        <Icon $hoverable onClick={onClick}>
            {enabled && <EyeIcon />}
            {!enabled && <EyeSlashIcon />}
        </Icon>
    );
};

const DialogDecorator = styled(DialogWindow)`
    width: 90%;
    height: 100%;
    flex-basis: unset;
`;

const ContainerDialog = styled.div`
    height: 100%;
    width: 100%;
    background: white;
    overflow-y: auto;
    display: flex;
    flex: 1;
`;

const Placements: FC<{}> = () => {
    const isEmbedded = new URLSearchParams(window.location.search).get('embedded') === 'true';
    const { modelId } = useParams();
    const { showDialog, closeDialog } = useDialogManager();

    if (!modelId) {
        throw new Error(`Invalid model id ${modelId}`);
    }

    const handleAddNewPlacement = () => {
        if (!modelId) return;
        showDialog(
            'new-placement',
            <Dialog windowDecorator={DialogDecorator} onClose={() => closeDialog('new-placement')}>
                <ContainerDialog>
                    <PlacementPage modelId={parseInt(modelId)} />
                </ContainerDialog>
            </Dialog>
        );
    };

    const data = useLazyLoadQuery<placementsQuery>(
        graphql`
            query placementsQuery($id: ID!) {
                placementsOutput(id: $id) {
                    id
                    modelID
                    isEnabled
                    name
                }
            }
        `,
        { id: `guid://Zakeke/Placements/${modelId}` },
        { fetchPolicy: 'network-only' }
    );

    const [deletePlacement] = useMutation<placementsDeleteMutation>(graphql`
        mutation placementsDeleteMutation($id: ID!) {
            deletePlacementOutput(id: $id) {
                placementOutput {
                    id
                }
            }
        }
    `);

    const [enablePlacement] = useMutation<placementsEnablePlacementMutation>(graphql`
        mutation placementsEnablePlacementMutation($id: ID!, $isEnabled: Boolean!) {
            enablePlacementOutput(id: $id, isEnabled: $isEnabled) {
                placementOutput {
                    id
                    isEnabled
                }
            }
        }
    `);

    const handleEditPlacementClick = (modelId: number, placementId: string) => {
        showDialog(
            'edit-placement',
            <Dialog windowDecorator={DialogDecorator} onClose={() => closeDialog('edit-placement')}>
                <ContainerDialog>
                    <PlacementPage modelId={modelId} placementId={toLocalID(placementId)} />
                </ContainerDialog>
            </Dialog>
        );
    };

    const handleEnablePlacementClick = (id: string, value: boolean) => {
        enablePlacement({
            variables: {
                id: id,
                isEnabled: value
            },
            onError: () => {
                showErrorNotification({ message: T._('Update failed!', 'Admin') });
            },
            onCompleted: () => {
                showSuccessNotification({ message: T._('Updated!', 'Admin') });
            }
        });
    };

    const handleDeletePlacementClick = (id: string, name: string) => {
        showDialog(
            'delete',
            <QuestionDialog
                message={stringFormat(T._('Do you want delete the advanced print-ready files "{0}"?', 'Admin'), name)}
                onYesClick={async () => {
                    closeDialog('delete');
                    const loadingNotification = showInfoNotification({ message: (T._('Deleting advanced print-ready files {0}', 'Admin'), name) });

                    deletePlacement({
                        variables: {
                            id: id
                        },
                        onError: () => {
                            removeNotification(loadingNotification);
                            showErrorNotification({ message: T._('Failed deleting advanced print-ready files', 'Admin') });
                        },
                        onCompleted: () => {
                            removeNotification(loadingNotification);
                            showSuccessNotification({ message: T._('Advanced print-ready files deleted.', 'Admin') });
                        },

                        updater: (store) => {
                            // //remove the deleted placement in the relay store
                            const root = store.getRoot();
                            const deletedPlacement = store.getRootField('deletePlacementOutput').getLinkedRecord('placementOutput');
                            const oldPlacements = store.getRoot().getLinkedRecords('placementsOutput', { id: `guid://Zakeke/Placements/${modelId}` });
                            const allPlacements = oldPlacements ? oldPlacements.filter((x) => x != deletedPlacement) : null;
                            if (allPlacements) root.setLinkedRecords(allPlacements, 'placementsOutput', { id: `guid://Zakeke/Placements/${modelId}` });
                        }
                    });
                }}
            />
        );
    };

    return (
        <React.Fragment>
            <VerticalLayout>
                {!isEmbedded && (
                    <PageHeader
                        title={T._('Advanced print-ready files', 'Admin')}
                        actions={
                            data.placementsOutput &&
                            data.placementsOutput.length > 0 && (
                                <PrimaryButton size='large' onClick={handleAddNewPlacement}>
                                    {T._('Add', 'Admin')}
                                </PrimaryButton>
                            )
                        }
                    ></PageHeader>
                )}
                <Section>
                    <Container>
                        {data.placementsOutput && data.placementsOutput.length > 0 && (
                            <Table
                                headings={
                                    [
                                        [T._('Name', 'Admin'), '1fr'],
                                        ['', 'auto']
                                    ] as TableHeading[]
                                }
                            >
                                {data.placementsOutput.map(({ id, isEnabled, name, modelID }) => {
                                    return (
                                        <Row
                                            key={id}
                                            cells={[
                                                <LinkCell style={{ width: '100%' }}>
                                                    <a onClick={() => handleEditPlacementClick(modelID, id)} role='button'>
                                                        {name}
                                                    </a>
                                                </LinkCell>,
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                                    <EditButton onClick={() => handleEditPlacementClick(modelID, id)} />
                                                    <EyeButton onClick={() => handleEnablePlacementClick(id, !isEnabled)} enabled={isEnabled} />
                                                    <DeleteButton onClick={() => handleDeletePlacementClick(id, name)} />
                                                </div>
                                            ]}
                                        />
                                    );
                                })}
                            </Table>
                        )}

                        {data.placementsOutput && data.placementsOutput.length === 0 && (
                            <EmptyList
                                title={'Create advanced production-ready files for your operations'}
                                subTitle={T._(
                                    'Create custom advanced print-ready files, such as nested and stacked designs, or simply add dynamic or fixed texts, QR codes, PDFs and much more.',
                                    'Admin'
                                )}
                                learnMoreLink='https://zakeke.zendesk.com/hc/en-us/articles/9686184040348'
                                buttonTitle={T._('Add', 'Admin')}
                                onButtonClick={handleAddNewPlacement}
                            />
                        )}
                    </Container>
                </Section>
            </VerticalLayout>
        </React.Fragment>
    );
};

export default Placements;
